import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, CheckCircle2, TrendingDown, Clock, MinusCircle, MessageCircle, Mail, History, ChevronDown, ChevronUp, FileText, Search, CalendarRange, X, Pencil, Trash2 } from 'lucide-react';
import { apiFetch } from '../utils/apiClient';
import { useNotification } from '../context/NotificationContext';
import { formatDateDDMMYYYY } from '../utils/formatDate';
import ConfirmModal from '../components/ConfirmModal';
import './SalaryStatus.css';

const statementPath = (employeeId, range) => {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  const qs = params.toString();
  return `/api/employees/${employeeId}/salary/statement.pdf${qs ? `?${qs}` : ''}`;
};

const todayStr = () => new Date().toISOString().split('T')[0];

// WhatsApp's link format needs the full international number. Employee
// phone numbers are stored as plain 10-digit Nepali mobile numbers with no
// country code, so add it (977) unless one's already present.
const normalizePhoneForWhatsApp = (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length === 10 ? `977${digits}` : digits;
};

const SalaryStatus = () => {
  const { notifySuccess, notifyError, showLoading, hideLoading } = useNotification();
  const [salaries, setSalaries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); 
  const [historyOpenFor, setHistoryOpenFor] = useState(null);
  const [historyData, setHistoryData] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [amountInput, setAmountInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [actionDate, setActionDate] = useState(todayStr());
  const [editPaymentModal, setEditPaymentModal] = useState(null); // { employeeId, paymentId, date }
  const [editAdvanceModal, setEditAdvanceModal] = useState(null); // { employeeId, advanceId, amount, date }
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmLabel, danger, onConfirm }
  const [statementRange, setStatementRange] = useState({ from: '', to: '' }); 
  const hasStatementRange = !!(statementRange.from || statementRange.to);

  const fetchSalaries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statementRange.from) params.set('from', statementRange.from);
      if (statementRange.to) params.set('to', statementRange.to);
      const qs = params.toString();
      const response = await apiFetch(`/api/employees/salaries/all${qs ? `?${qs}` : ''}`);
      if (response.ok) {
        const data = await response.json();
        setSalaries(data);
      }
    } catch (e) {
      console.error('Error fetching salaries');
    }
  }, [statementRange.from, statementRange.to]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const openAdvanceModal = (salary) => {
    setActiveModal({ type: 'advance', employeeId: salary.id, employeeName: salary.name });
    setAmountInput('');
    setReasonInput('');
    setActionDate(todayStr());
  };

  const openOvertimeModal = (salary) => {
    setActiveModal({ type: 'overtime', employeeId: salary.id, employeeName: salary.name });
    setAmountInput('');
    setReasonInput('');
    setActionDate(todayStr());
  };

  const openBonusModal = (salary) => {
    setActiveModal({ type: 'bonus', employeeId: salary.id, employeeName: salary.name });
    setAmountInput('');
    setReasonInput('');
    setActionDate(todayStr());
  };

  const openDeductionModal = (salary) => {
    setActiveModal({ type: 'deduction', employeeId: salary.id, employeeName: salary.name });
    setAmountInput('');
    setReasonInput('');
    setActionDate(todayStr());
  };

  const openPayModal = (salary) => {
    setActiveModal({ type: 'pay', employeeId: salary.id, employeeName: salary.name });
    setActionDate(todayStr());
  };

  const closeModal = () => setActiveModal(null);

  const submitAmountEntry = async (e) => {
    e.preventDefault();
    if (!amountInput || isNaN(amountInput) || Number(amountInput) === 0) return;

    const endpoint = activeModal.type;
    const isDeductionReduction = endpoint === 'deduction' && Number(amountInput) < 0;
    const label = isDeductionReduction ? 'deduction reduction' : endpoint;
    showLoading(`Saving ${label}…`);
    try {
      const response = await apiFetch(`/api/employees/${activeModal.employeeId}/salary/${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Number(amountInput), date: actionDate, reason: reasonInput || undefined })
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        closeModal();
        fetchSalaries();
        notifySuccess(
          isDeductionReduction ? 'Deduction reduced successfully'
          : endpoint === 'bonus' ? 'Bonus paid successfully'
          : endpoint === 'overtime' && data.paidImmediately ? "Overtime paid immediately — this month's salary was already paid, so it wasn't added to next month"
          : `${label.charAt(0).toUpperCase()}${label.slice(1)} added successfully`
        );
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || `Failed to add ${label}`);
      }
    } catch (e) {
      notifyError(`Failed to add ${label}`);
    } finally {
      hideLoading();
    }
  };

  const submitPay = async () => {
    showLoading('Marking salary as paid…');
    try {
      const response = await apiFetch(`/api/employees/${activeModal.employeeId}/salary/pay`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: actionDate })
      });
      if (response.ok) {
        closeModal();
        fetchSalaries();
        notifySuccess('Salary marked as paid');
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || 'Failed to mark salary as paid');
      }
    } catch (e) {
      notifyError('Failed to mark salary as paid');
    } finally {
      hideLoading();
    }
  };

  const refetchHistory = async (employeeId) => {
    try {
      const response = await apiFetch(`/api/employees/${employeeId}/salary/history`);
      if (response.ok) {
        const data = await response.json();
        setHistoryData(prev => ({ ...prev, [employeeId]: data }));
      }
    } catch (e) {
      console.error('Failed to refresh salary history');
    }
  };

  const openEditPaymentModal = (employeeId, item) => {
    setEditPaymentModal({ employeeId, paymentId: item.id, date: item.event_date.split('T')[0] });
  };

  const submitEditPaymentDate = async (e) => {
    e.preventDefault();
    showLoading('Updating payment date…');
    try {
      const response = await apiFetch(`/api/employees/${editPaymentModal.employeeId}/salary/payment/${editPaymentModal.paymentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: editPaymentModal.date })
      });
      if (response.ok) {
        const employeeId = editPaymentModal.employeeId;
        setEditPaymentModal(null);
        refetchHistory(employeeId);
        notifySuccess('Payment date updated');
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || 'Failed to update payment date');
      }
    } catch (e) {
      notifyError('Failed to update payment date');
    } finally {
      hideLoading();
    }
  };

  const performDelete = async ({ url, employeeId, successMessage, failMessage }) => {
    showLoading('Deleting…');
    try {
      const response = await apiFetch(url, { method: 'DELETE' });
      if (response.ok) {
        refetchHistory(employeeId);
        fetchSalaries();
        notifySuccess(successMessage);
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || failMessage);
      }
    } catch (e) {
      notifyError(failMessage);
    } finally {
      hideLoading();
    }
  };

  // Restricted server-side to the employee's MOST RECENT payment — restores
  // the advance/overtime/deduction balances and next pay date from the
  // snapshot that payment itself logged, so this is meant for undoing a
  // mistake right after it happens, not general history editing.
  const handleDeletePayment = (employeeId, item) => {
    setConfirmModal({
      title: 'Undo This Payment?',
      message: `Undo this payment of Rs${Number(item.amount).toFixed(2)}? This restores the advance/overtime/deduction balances from before it was paid and reopens this cycle as unpaid. Only works on the most recent payment.`,
      confirmLabel: 'Undo Payment',
      danger: true,
      onConfirm: () => {
        setConfirmModal(null);
        performDelete({
          url: `/api/employees/${employeeId}/salary/payment/${item.id}`,
          employeeId,
          successMessage: 'Payment undone — balances restored',
          failMessage: 'Failed to undo payment'
        });
      }
    });
  };

  const openEditAdvanceModal = (employeeId, item) => {
    setEditAdvanceModal({ employeeId, advanceId: item.id, amount: String(Number(item.amount)), date: item.event_date.split('T')[0] });
  };

  const submitEditAdvance = async (e) => {
    e.preventDefault();
    if (!editAdvanceModal.amount || isNaN(editAdvanceModal.amount) || Number(editAdvanceModal.amount) <= 0) return;
    showLoading('Updating advance…');
    try {
      const response = await apiFetch(`/api/employees/${editAdvanceModal.employeeId}/salary/advance/${editAdvanceModal.advanceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Number(editAdvanceModal.amount), date: editAdvanceModal.date })
      });
      if (response.ok) {
        const employeeId = editAdvanceModal.employeeId;
        setEditAdvanceModal(null);
        refetchHistory(employeeId);
        fetchSalaries();
        notifySuccess('Advance updated');
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || 'Failed to update advance');
      }
    } catch (e) {
      notifyError('Failed to update advance');
    } finally {
      hideLoading();
    }
  };

  // Restricted server-side to advances that haven't been paid out in a
  // completed cycle yet (i.e. given after the employee's latest payment) —
  // once "Pay Full" runs, the amount is already baked into that payment's
  // snapshot, so editing it afterward would incorrectly touch the new cycle.
  const handleDeleteAdvance = (employeeId, item) => {
    setConfirmModal({
      title: 'Delete This Advance?',
      message: `Delete this advance of Rs${Number(item.amount).toFixed(2)}? This reduces the employee's current advance balance by that amount.`,
      confirmLabel: 'Delete Advance',
      danger: true,
      onConfirm: () => {
        setConfirmModal(null);
        performDelete({
          url: `/api/employees/${employeeId}/salary/advance/${item.id}`,
          employeeId,
          successMessage: 'Advance deleted',
          failMessage: 'Failed to delete advance'
        });
      }
    });
  };

  const fetchStatementBlob = async (salary) => {
    const response = await apiFetch(statementPath(salary.id, statementRange));
    if (!response.ok) throw new Error('Failed to fetch PDF');
    return response.blob();
  };

  const downloadStatementPdf = async (salary) => {
    const blob = await fetchStatementBlob(salary);
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `salary-statement-${salary.name.replace(/\s+/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  };

  const viewStatementPdf = async (salary) => {
   
    const newWindow = window.open('', '_blank');
    showLoading('Loading PDF…');
    try {
      const blob = await fetchStatementBlob(salary);
      const blobUrl = URL.createObjectURL(blob);
      if (newWindow) newWindow.location.href = blobUrl;
    } catch (e) {
      if (newWindow) newWindow.close();
      notifyError('Failed to load PDF statement');
    } finally {
      hideLoading();
    }
  };

  const handleSendWhatsApp = async (salary) => {
    if (!salary.phone) {
      notifyError("Employee does not have a phone number assigned.");
      return;
    }

    const waPhone = normalizePhoneForWhatsApp(salary.phone);
    const message = `Hello ${salary.name}, your Lingbari salary statement is attached.`;

    showLoading('Downloading PDF statement…');
    try {
      // Same flow on every platform: download the PDF, then open the
      // employee's actual WhatsApp chat directly (wa.me deep-links into the
      // app on iOS/Android and redirects to WhatsApp Web on desktop — unlike
      // web.whatsapp.com, which only works from a desktop browser). WhatsApp
      // has no API to pre-attach a file to a specific chat, so attaching the
      // downloaded PDF from the chat that opens is still a manual last step.
      await downloadStatementPdf(salary);
      window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`, '_blank');
      notifySuccess('PDF downloaded — attach it in the WhatsApp chat and send.');
    } catch (e) {
      notifyError('WhatsApp opened, but the PDF download failed — use "View PDF Statement" to save it manually.');
    } finally {
      hideLoading();
    }
  };

  const handleSendEmail = async (salary) => {
    if (!salary.email) {
      notifyError("Employee does not have an email assigned.");
      return;
    }
    showLoading('Sending email…');
    try {
      const response = await apiFetch(`/api/communication/email/${salary.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: statementRange.from || undefined, to: statementRange.to || undefined })
      });
      if (response.ok) {
        notifySuccess("Statement emailed successfully!");
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || "Failed to send email");
      }
    } catch (e) {
      notifyError('Failed to send email');
    } finally {
      hideLoading();
    }
  };

  const toggleHistory = async (id) => {
    if (historyOpenFor === id) {
      setHistoryOpenFor(null);
      return;
    }
    setHistoryOpenFor(id);
    if (!historyData[id]) {
      try {
        const response = await apiFetch(`/api/employees/${id}/salary/history`);
        if (response.ok) {
          const data = await response.json();
          setHistoryData(prev => ({ ...prev, [id]: data }));
        }
      } catch (e) {
        console.error('Failed to fetch salary history');
      }
    }
  };

  const isDueSoon = (dateString) => {
    if (!dateString) return false;
    const due = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(due - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 3;
  };

  const filteredSalaries = salaries.filter(salary => {
    const matchesSearch = salary.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
    const isPaid = !!salary.is_paid;
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'paid' ? isPaid : !isPaid);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="salary-page">
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700' }}>Salary Ledger</h1>
        <p style={{ color: 'var(--text-muted)' }}>Manage advances and monthly payouts</p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{ position: 'relative', maxWidth: '360px', flex: '1 1 260px' }}>
          <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            className="input-field"
            style={{ paddingLeft: '42px' }}
            placeholder="Search employee by name…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {['all', 'paid', 'pending'].map(f => (
            <button
              key={f}
              className="btn"
              style={{
                background: statusFilter === f ? 'var(--primary-color)' : 'var(--surface-color)',
                color: statusFilter === f ? 'white' : 'var(--text-main)',
                border: '1px solid var(--border-color)',
                padding: '10px 20px',
                fontSize: '13px',
                textTransform: 'capitalize'
              }}
              onClick={() => setStatusFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '14px 20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: '500' }}>
          <CalendarRange size={16} />
          Statement Range
        </div>
        <input
          type="date"
          className="input-field"
          style={{ padding: '8px 10px', fontSize: '13px', width: 'auto' }}
          max={statementRange.to || todayStr()}
          value={statementRange.from}
          onChange={e => setStatementRange(prev => ({ ...prev, from: e.target.value }))}
        />
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>to</span>
        <input
          type="date"
          className="input-field"
          style={{ padding: '8px 10px', fontSize: '13px', width: 'auto' }}
          min={statementRange.from}
          max={todayStr()}
          value={statementRange.to}
          onChange={e => setStatementRange(prev => ({ ...prev, to: e.target.value }))}
        />
        {hasStatementRange ? (
          <button
            onClick={() => setStatementRange({ from: '', to: '' })}
            style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
          >
            <X size={14} /> Clear
          </button>
        ) : (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Applies to every "View PDF", "WhatsApp", and "Email" action below. Leave blank for full history.</span>
        )}
      </div>

      {filteredSalaries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          No employee found matching the current search/filter.
        </div>
      ) : (
      <div className="salary-cards-grid">
        {filteredSalaries.map(salary => {
          const dueWarning = isDueSoon(salary.next_paying_date);
          // Bonus is paid immediately (shown below, logged straight to Payment
          // History) — it never accumulates into Remaining Payout.
          const remaining = Number(salary.base_salary) + Number(salary.overtime_amount || 0) - Number(salary.advance_taken) - Number(salary.deduction_amount || 0);
          const isPaid = !!salary.is_paid;

          return (
            <div className="card" key={salary.id} style={{ position: 'relative', overflow: 'hidden' }}>
              {dueWarning && !isPaid && (
                <div style={{ position: 'absolute', top: 0, right: 0, background: 'var(--warning-color)', color: 'white', padding: '4px 12px', fontSize: '12px', fontWeight: '600', borderBottomLeftRadius: '8px', boxShadow: '10px 11px 16px rgba(0, 0, 0, 0.15)' }}>
                  Due Soon
                </div>
              )}
              {isPaid && (
                <div style={{ position: 'absolute', top: 0, right: 0, background: 'var(--success-color)', color: 'white', padding: '4px 12px', fontSize: '12px', fontWeight: '600', borderBottomLeftRadius: '8px', boxShadow: '10px 11px 16px rgba(0, 0, 0, 0.15)' }}>
                  Paid
                </div>
              )}
              {!isPaid && !dueWarning && (
                <div style={{ position: 'absolute', top: 0, right: 0, background: 'var(--text-muted)', color: 'white', padding: '4px 12px', fontSize: '12px', fontWeight: '600', borderBottomLeftRadius: '8px', boxShadow: '10px 11px 16px rgba(0, 0, 0, 0.15)' }}>
                  Pending
                </div>
              )}

              <h3 style={{ fontSize: '18px', marginBottom: '16px' }}>{salary.name}</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Base Salary</span>
                  <span style={{ fontWeight: '600' }}>Rs{salary.base_salary}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Overtime</span>
                  <span style={{ color: 'var(--success-color)' }}>+Rs{salary.overtime_amount || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    Bonus Paid
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '6px' }}>
                      ({hasStatementRange ? 'selected range' : 'all time'})
                    </span>
                  </span>
                  <span style={{ color: 'var(--success-color)' }}>+Rs{salary.bonus_in_range || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Advance Taken</span>
                  <span style={{ color: 'var(--danger-color)' }}>-Rs{salary.advance_taken}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Deductions (Late/Break)</span>
                  <span style={{ color: 'var(--danger-color)' }}>-Rs{salary.deduction_amount || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-color)', paddingTop: '12px', fontSize: '16px' }}>
                  <span style={{ fontWeight: '600' }}>Remaining Payout</span>
                  <span style={{ fontWeight: '700' }}>Rs{remaining}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginTop: '4px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Next Pay Date</span>
                  <span>{salary.next_paying_date ? formatDateDDMMYYYY(salary.next_paying_date) : 'N/A'}</span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <button
                  className="btn"
                  style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger-color)', width: '100%', padding: '10px 4px', fontSize: '13px' }}
                  onClick={() => openAdvanceModal(salary)}
                  title="Advance can be given anytime — it's deducted from the next unpaid cycle."
                >
                  <TrendingDown size={16} style={{ marginRight: '6px' }} />
                  Advance
                </button>
                <button
                  className="btn"
                  style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--success-color)', width: '100%', padding: '10px 4px', fontSize: '13px' }}
                  onClick={() => openOvertimeModal(salary)}
                  title="Overtime past shift end is added automatically — use this only for manual corrections. If this month is already marked Paid, it's paid out immediately instead of rolling into next month."
                >
                  <Clock size={16} style={{ marginRight: '6px' }} />
                  Overtime
                </button>
                <button
                  className="btn"
                  style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7', width: '100%', padding: '10px 4px', fontSize: '13px' }}
                  onClick={() => openBonusModal(salary)}
                  title="A one-off bonus paid immediately — e.g. a festival or performance bonus. Not part of Remaining Payout."
                >
                  <DollarSign size={16} style={{ marginRight: '6px' }} />
                  Bonus
                </button>
                <button
                  className="btn"
                  style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger-color)', width: '100%', padding: '10px 4px', fontSize: '13px' }}
                  onClick={() => openDeductionModal(salary)}
                  title="Late check-in/long break deductions are applied automatically. Enter a positive amount to add a deduction, or a negative amount to reduce/waive one already applied."
                >
                  <MinusCircle size={16} style={{ marginRight: '6px' }} />
                  Deduction
                </button>
                {!isPaid && (
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', background: 'var(--success-color)', padding: '10px 4px', fontSize: '13px' }}
                    onClick={() => openPayModal(salary)}
                  >
                    <CheckCircle2 size={16} style={{ marginRight: '6px' }} />
                    Pay Full
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed var(--border-color)' }}>
                <button
                  className="btn"
                  style={{ background: 'rgba(37, 211, 102, 0.1)', color: '#25D366', width: '100%', padding: '8px' }}
                  onClick={() => handleSendWhatsApp(salary)}
                >
                  <MessageCircle size={16} style={{ marginRight: '6px' }} />
                  WhatsApp
                </button>
                <button
                  className="btn"
                  style={{ background: 'rgba(37, 99, 235, 0.1)', color: 'var(--primary-color)', width: '100%', padding: '8px' }}
                  onClick={() => handleSendEmail(salary)}
                >
                  <Mail size={16} style={{ marginRight: '6px' }} />
                  Email
                </button>
              </div>

              <button
                className="btn"
                style={{ background: 'rgba(37, 99, 235, 0.1)', color: 'var(--primary-color)', width: '100%', padding: '8px', marginTop: '12px' }}
                onClick={() => viewStatementPdf(salary)}
              >
                <FileText size={16} style={{ marginRight: '6px' }} />
                View PDF Statement
              </button>

              <button
                className="btn"
                style={{ background: 'none', color: 'var(--text-muted)', width: '100%', padding: '8px', marginTop: '8px' }}
                onClick={() => toggleHistory(salary.id)}
              >
                <History size={16} style={{ marginRight: '6px' }} />
                Payment History
                {historyOpenFor === salary.id ? <ChevronUp size={16} style={{ marginLeft: '6px' }} /> : <ChevronDown size={16} style={{ marginLeft: '6px' }} />}
              </button>

              {historyOpenFor === salary.id && (
                <div style={{ marginTop: '8px', borderTop: '1px dashed var(--border-color)', paddingTop: '12px', maxHeight: '180px', overflowY: 'auto' }}>
                  {!historyData[salary.id] ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center' }}>Loading…</p>
                  ) : historyData[salary.id].length === 0 ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center' }}>No history recorded yet.</p>
                  ) : (
                    (() => {
                      // Edit/Delete only ever succeed on the employee's most
                      // recent payment, and separately on their most recent
                      // advance (the server enforces both) — so only show
                      // the icons where they'd actually work, rather than
                      // let an older entry look editable and then just
                      // reject the click.
                      const paymentItems = historyData[salary.id].filter(h => h.type === 'payment');
                      const latestPaymentId = paymentItems.length ? Math.max(...paymentItems.map(p => p.id)) : null;
                      const advanceItems = historyData[salary.id].filter(h => h.type === 'advance');
                      const latestAdvanceId = advanceItems.length ? Math.max(...advanceItems.map(a => a.id)) : null;
                      return historyData[salary.id].map(item => {
                      const isDeductionReduction = item.type === 'deduction' && Number(item.amount) < 0;
                      const isOvertimePaidSeparately = item.type === 'overtime' && Number(item.applied_to_cycle) === 0;
                      const isEditablePayment = item.type === 'payment' && item.id === latestPaymentId;
                      const isEditableAdvance = item.type === 'advance' && item.id === latestAdvanceId;
                      const label = item.type === 'advance' ? 'Advance Given'
                        : isOvertimePaidSeparately ? 'Overtime Paid'
                        : item.type === 'overtime' ? 'Overtime Added'
                        : item.type === 'bonus' ? 'Bonus Paid'
                        : isDeductionReduction ? 'Deduction Reduced'
                        : item.type === 'deduction' ? 'Deduction Applied'
                        : 'Full Salary Paid';
                      return (
                        <div key={`${item.type}-${item.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', padding: '6px 0' }}>
                          <span style={{ color: 'var(--text-muted)' }}>
                            {formatDateDDMMYYYY(item.event_date)} — {label}
                            {item.reason && <span style={{ fontStyle: 'italic' }}> ({item.reason})</span>}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '600', color: isDeductionReduction ? 'var(--success-color)' : undefined }}>
                              {isDeductionReduction ? '+' : ''}Rs{Math.abs(Number(item.amount)).toFixed(2)}
                            </span>
                            {isEditablePayment && (
                              <>
                                <button
                                  className="icon-btn"
                                  style={{ color: 'var(--text-muted)', padding: '2px' }}
                                  title="Correct the date this was paid"
                                  onClick={() => openEditPaymentModal(salary.id, item)}
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  className="icon-btn"
                                  style={{ color: 'var(--danger-color)', padding: '2px' }}
                                  title="Undo this payment"
                                  onClick={() => handleDeletePayment(salary.id, item)}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </>
                            )}
                            {isEditableAdvance && (
                              <>
                                <button
                                  className="icon-btn"
                                  style={{ color: 'var(--text-muted)', padding: '2px' }}
                                  title="Correct the amount or date of this advance"
                                  onClick={() => openEditAdvanceModal(salary.id, item)}
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  className="icon-btn"
                                  style={{ color: 'var(--danger-color)', padding: '2px' }}
                                  title="Delete this advance"
                                  onClick={() => handleDeleteAdvance(salary.id, item)}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </>
                            )}
                          </span>
                        </div>
                      );
                    });
                    })()
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {activeModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeModal}
        >
          <div className="card" style={{ width: '360px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            {(activeModal.type === 'advance' || activeModal.type === 'overtime' || activeModal.type === 'bonus' || activeModal.type === 'deduction') ? (
              <form onSubmit={submitAmountEntry}>
                <h3 style={{ marginBottom: '16px' }}>
                  {activeModal.type === 'advance' ? 'Give Advance'
                    : activeModal.type === 'overtime' ? 'Add Overtime'
                    : activeModal.type === 'bonus' ? 'Pay Bonus'
                    : 'Adjust Deduction'} — {activeModal.employeeName}
                </h3>
                <div style={{ marginBottom: '16px' }}>
                  <label className="label">Amount</label>
                  <input
                    type="number"
                    className="input-field"
                    required
                    autoFocus
                    step="0.01"
                    min={activeModal.type === 'deduction' ? undefined : '0.01'}
                    value={amountInput}
                    onChange={e => setAmountInput(e.target.value)}
                  />
                  {activeModal.type === 'deduction' && (
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Positive adds a deduction. Negative reduces/waives one already applied (e.g. correcting an automatic late/break deduction).
                    </p>
                  )}
                </div>
                {(activeModal.type === 'bonus' || activeModal.type === 'deduction') && (
                  <div style={{ marginBottom: '16px' }}>
                    <label className="label">Reason (optional)</label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder={activeModal.type === 'bonus' ? 'e.g. festival bonus, performance' : 'e.g. waived late deduction, entry correction'}
                      value={reasonInput}
                      onChange={e => setReasonInput(e.target.value)}
                    />
                  </div>
                )}
                <div style={{ marginBottom: '20px' }}>
                  <label className="label">Date</label>
                  <input
                    type="date"
                    className="input-field"
                    required
                    max={todayStr()}
                    value={actionDate}
                    onChange={e => setActionDate(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save</button>
                </div>
              </form>
            ) : (
              <div>
                <h3 style={{ marginBottom: '16px' }}>Mark Salary Paid — {activeModal.employeeName}</h3>
                <div style={{ marginBottom: '20px' }}>
                  <label className="label">Payment Date</label>
                  <input
                    type="date"
                    className="input-field"
                    required
                    max={todayStr()}
                    value={actionDate}
                    onChange={e => setActionDate(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                  <button type="button" className="btn btn-primary" style={{ background: 'var(--success-color)' }} onClick={submitPay}>
                    Confirm Paid
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {editPaymentModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditPaymentModal(null)}
        >
          <div className="card" style={{ width: '360px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <form onSubmit={submitEditPaymentDate}>
              <h3 style={{ marginBottom: '16px' }}>Correct Payment Date</h3>
              <div style={{ marginBottom: '20px' }}>
                <label className="label">Paid On</label>
                <input
                  type="date"
                  className="input-field"
                  required
                  max={todayStr()}
                  value={editPaymentModal.date}
                  onChange={e => setEditPaymentModal(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn" onClick={() => setEditPaymentModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editAdvanceModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditAdvanceModal(null)}
        >
          <div className="card" style={{ width: '360px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <form onSubmit={submitEditAdvance}>
              <h3 style={{ marginBottom: '16px' }}>Correct Advance</h3>
              <div style={{ marginBottom: '16px' }}>
                <label className="label">Amount</label>
                <input
                  type="number"
                  className="input-field"
                  required
                  autoFocus
                  value={editAdvanceModal.amount}
                  onChange={e => setEditAdvanceModal(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label className="label">Date</label>
                <input
                  type="date"
                  className="input-field"
                  required
                  max={todayStr()}
                  value={editAdvanceModal.date}
                  onChange={e => setEditAdvanceModal(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn" onClick={() => setEditAdvanceModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default SalaryStatus;
